import React, { useState, useEffect, useRef } from 'react';
import {
  Server,
  Database,
  Cpu,
  ShieldCheck,
  Terminal,
  Layers,
  GitBranch,
  ArrowRight,
  CheckCircle2,
  Activity,
  ExternalLink,
  Mail,
  Phone,
  MapPin,
  Linkedin,
  Copy,
  Check,
  Menu,
  X,
  ChevronRight,
  Code2,
  Zap,
  Lock,
  RefreshCw,
  Sliders,
  Globe,
  Building2,
  Calendar,
  FileText,
  Play,
  Radio,
  HardDrive,
  Network,
  Workflow,
  Sparkles,
  Search,
  CheckSquare,
  AlertTriangle
} from 'lucide-react';

const ARCHITECTURE_NODES = [
  {
    id: 'client',
    title: 'Client & Frontend Consumer Layer',
    type: 'API Consumer',
    icon: Globe,
    tag: 'REST APIs / Mobile / Web',
    shortDesc: 'Mobile applications, E-commerce frontends, LMS portals, and School Management clients.',
    specifications: [
      'Stateless RESTful JSON endpoint interfaces',
      'Unified response conventions across platforms',
      'Cross-Origin Resource Sharing (CORS) enforcement',
      'Rate-limiting and throttling per client token'
    ],
    cvHighlight: 'Engineered secure API layers supporting mobile and web frontend integrations for 600+ ERP endpoints.',
    tech: ['RESTful API Design', 'Postman', 'JSON Schema', 'Sanctum Tokens']
  },
  {
    id: 'security',
    title: 'Security, Auth & Edge Proxy',
    type: 'Edge & Auth',
    icon: ShieldCheck,
    tag: 'Sanctum / SSL / Cloudflare',
    shortDesc: 'Token authentication, edge network security, SSL termination, and server environment isolation.',
    specifications: [
      'Laravel Sanctum API token authentication',
      'Cloudflare DNS & DDOS protection layer',
      'Contabo Linux server hardening & SSL certificates',
      'Environment-isolated cPanel/WHM configuration'
    ],
    cvHighlight: 'Managed Contabo servers, CORS handling, and SSL management to protect sensitive financial and personal data.',
    tech: ['Laravel Sanctum', 'Cloudflare', 'Linux (Contabo)', 'cPanel/WHM', 'SSL/CORS']
  },
  {
    id: 'core',
    title: 'Core Application & DDD Domain Logic',
    type: 'Business Logic',
    icon: Cpu,
    tag: 'Domain-Driven Design / Service Layer',
    shortDesc: 'Modular DDD architecture separating business domain logic, services, and repositories.',
    specifications: [
      'Domain-Driven Design (DDD) module structure',
      'Service Layer pattern isolating complex business logic',
      'Repository Pattern for clean data abstraction',
      'PSR-12 standard compliant backend codebase'
    ],
    cvHighlight: 'Applied DDD-structured modules and service-layer patterns across legacy Symfony migrations and ERP systems.',
    tech: ['PHP 8.2+', 'Laravel 10/11', 'DDD Architecture', 'Repository Pattern', 'PSR-12']
  },
  {
    id: 'queue',
    title: 'High-Concurrency Queue & Async Engine',
    type: 'Async Processing',
    icon: Workflow,
    tag: 'Laravel Queues / Background Workers',
    shortDesc: 'Non-blocking background job execution, overlap protection, and heavy calculation queues.',
    specifications: [
      'Laravel Queue Workers with Redis/Database drivers',
      'Atomic job lock & overlap protection for critical operations',
      'Automated material cost recalculations without downtime',
      'Financial report batch processing in background'
    ],
    cvHighlight: 'Optimized job processing by eliminating N+1 queries and adding overlap protection to critical queue jobs.',
    tech: ['Laravel Queues', 'Background Workers', 'Atomic Locks', 'Batch Jobs']
  },
  {
    id: 'database',
    title: 'Multi-Tenant Data Layer',
    type: 'Persistence',
    icon: Database,
    tag: 'stancl/tenancy / MySQL / 200+ Tables',
    shortDesc: 'Multi-tenant database isolation, complex schema indexing, and query performance refactoring.',
    specifications: [
      'Data isolation via stancl/tenancy architecture',
      '200+ relational database tables schema design',
      'Strategic B-Tree indexing and query optimization',
      'Elimination of N+1 database bottlenecks'
    ],
    cvHighlight: 'Engineered multi-tenant ERP infrastructure with 200+ tables; improved response times by 60% through query refactoring.',
    tech: ['MySQL / MariaDB', 'stancl/tenancy', 'Query Optimization', 'Schema Indexing']
  },
  {
    id: 'integrations',
    title: 'Enterprise Integration Gateway',
    type: 'Integrations',
    icon: Network,
    tag: 'ERPNext / Gateways / Logistics / WhatsApp',
    shortDesc: 'Robust third-party connector layer for enterprise systems, payment gateways, and messaging bridges.',
    specifications: [
      'ERPNext bi-directional synchronization layer',
      'MyFatoorah & HyperPay financial payment processing',
      'Aramex shipping, tracking & logistics API integration',
      'Custom Laravel + Node.js WhatsApp Web gateway bridge'
    ],
    cvHighlight: 'Integrated ERPNext, MyFatoorah, Aramex, VerifyWay, HyperPay, and UltraMsg WhatsApp API into active platforms.',
    tech: ['ERPNext API', 'MyFatoorah', 'HyperPay', 'Aramex', 'Node.js WhatsApp Bridge', 'VerifyWay']
  }
];

const PROJECTS = [
  {
    id: 'araba-migration',
    title: 'Araba Platform Migration & Enterprise Integration Layer',
    badge: 'Symfony → Laravel Migration',
    domain: 'E-Commerce, Accounting & School Management',
    role: 'Lead Backend Developer & Systems Architect',
    summary: 'Led the live migration of a high-traffic multi-module Symfony/Doctrine application to a modern Laravel platform while keeping operations uninterrupted. Constructed external integration bridges for enterprise logistics, payment, and ERP systems.',
    problem: 'The legacy Symfony platform faced high coupling, slow database response times, difficult maintenance, and lacked modular separation for e-commerce, school management, and accounting domains.',
    solution: 'Re-architected the system into Laravel using Domain-Driven Design (DDD). Rebuilt core modules including address management, wishlists, commission configurations, invoice payments, and built clean REST APIs for multi-service integration.',
    tech: ['PHP', 'Laravel', 'Symfony (Legacy)', 'ERPNext API', 'MyFatoorah', 'Aramex API', 'VerifyWay', 'MySQL', 'Sanctum'],
    metrics: [
      { label: 'System Downtime During Migration', value: '0 Hours' },
      { label: 'Integrated Third-Party APIs', value: '4 Major Services' },
      { label: 'Core Modules Converted', value: '5 Primary Domains' }
    ],
    architectureDetails: 'Applied DDD-structured modules, service-layer patterns, Sanctum authentication, and consistent API response conventions. Optimized background material cost jobs by eliminating N+1 queries.'
  },
  {
    id: 'material-cost-engine',
    title: 'Material Cost & High-Concurrency Financial Engine',
    badge: 'Core ERP Calculation Subsystem',
    domain: 'Financial Accounting & Inventory ERP',
    role: 'ERP Backend Architect',
    summary: 'Engineered a high-precision financial engine for material cost calculations across inventory, restaurant operations, and school management modules under high concurrent transaction loads.',
    problem: 'Simultaneous inventory adjustments and material recalculations caused database locks, concurrency collisions, race conditions, and precision discrepancies in financial auditing.',
    solution: 'Designed an asynchronous queue processing system in Laravel with atomic lock protection. Built background worker pipelines with batching to calculate raw material costs in real time without blocking HTTP requests.',
    tech: ['Laravel Queues', 'MySQL Indexing', 'Atomic Locking', 'Background Jobs', 'PHP Decimal Math', 'Service Layer Pattern'],
    metrics: [
      { label: 'Financial Calculation Precision', value: '100% Accurate' },
      { label: 'Concurrency Bottlenecks Resolved', value: 'Zero Overlaps' },
      { label: 'ERP Sub-Systems Covered', value: 'Inventory / LMS / Dining' }
    ],
    architectureDetails: 'Eliminated heavy synchronous calculations from HTTP controllers. Introduced atomic job lock keys and query batching to prevent race conditions during bulk material price updates.'
  },
  {
    id: 'whatsapp-gateway',
    title: 'Custom WhatsApp API Automated Gateway Layer',
    badge: 'Microservice Bridge',
    domain: 'Automated Messaging & Notification System',
    role: 'Backend & Integration Engineer',
    summary: 'Architected a custom hybrid notification bridge using Laravel as the administrative & business rule manager, paired with a Node.js microservice handling the WhatsApp Web protocol.',
    problem: 'Third-party messaging SaaS providers carried prohibitive per-message fees and rigid API rate limits for high-volume transactional notifications like school alerts and payment receipts.',
    solution: 'Built a custom integration layer where Laravel queues system events and dispatches payloads to a Node.js process managing session persistence and socket connections to WhatsApp.',
    tech: ['Laravel API', 'Node.js', 'WhatsApp Web Protocol', 'UltraMsg', 'REST Webhooks', 'Async Queues'],
    metrics: [
      { label: 'Notification Delivery Latency', value: '< 1.5s' },
      { label: 'System Automation', value: '100% Unattended' },
      { label: 'Third-Party SaaS Cost Saved', value: 'Significant' }
    ],
    architectureDetails: 'Decoupled system triggers from protocol handlers. Laravel handles tenant permissions, message templating, and event queueing while Node.js manages execution.'
  },
  {
    id: 'school-lms-erp',
    title: 'Multi-Module School Management & LMS Platform',
    badge: 'Automated Workflow Subsystem',
    domain: 'Education & Enterprise Resource Planning',
    role: 'Full Stack Backend Developer',
    summary: 'Automated complex administrative and logistical workflows for school operations including student exams, automated bus route scheduling, and employee HR processes.',
    problem: 'Manual paper-based scheduling and disjoined exam grade compilation consumed hundreds of administrative hours each academic term.',
    solution: 'Designed structured database schemas and workflow state machines in Laravel to automate exam schedule generation, student bus route assignments, and payroll calculations.',
    tech: ['Laravel', 'MySQL', 'Multi-Tenancy', 'RESTful API', 'Sanctum Auth', 'Role-Based ACL'],
    metrics: [
      { label: 'Administrative Workload Reduction', value: '40%' },
      { label: 'Student & Bus Route Automated', value: 'Full Automation' },
      { label: 'Database Tables Integrated', value: 'Part of 200+ ERP' }
    ],
    architectureDetails: 'Integrated directly into the multi-tenant ERP platform, ensuring that tuition fees, staff payroll, and operational costs automatically synchronize with the central accounting ledger.'
  }
];

const EXPERIENCE_LOG = [
  {
    period: '2026 – PRESENT',
    role: 'Backend Developer',
    company: 'Nexora Tech',
    location: 'Damascus, Syria',
    type: 'Full-time / Enterprise Systems',
    status: 'ACTIVE RELEASE',
    version: 'v3.2.0-RELEASE',
    highlights: [
      'Led the migration of the Araba e-commerce application from Symfony/Doctrine to Laravel.',
      'Converted core domain modules including address management, wishlists, payment providers, commission configurations, and invoice processing.',
      'Built multi-service integration bridges connecting Araba to ERPNext, MyFatoorah, Aramex, and VerifyWay.',
      'Applied Domain-Driven Design (DDD) module organization, service-layer patterns, Sanctum authentication, and strict API response conventions.',
      'Optimized heavy queue job processing (material cost recalculation) by eliminating N+1 database queries and introducing overlap protection.'
    ],
    stack: ['Laravel', 'Symfony', 'ERPNext', 'MyFatoorah', 'Aramex', 'VerifyWay', 'DDD', 'Sanctum', 'MySQL']
  },
  {
    period: 'JAN 2023 – PRESENT',
    role: 'Backend Developer',
    company: 'Incentive',
    location: 'Damascus, Syria',
    type: 'Full-time / ERP Architecture',
    status: 'STABLE BRANCH',
    version: 'v2.8.5-PRODUCTION',
    highlights: [
      'Engineered a comprehensive enterprise ERP platform featuring 200+ tables and 600+ RESTful APIs across Accounting, Inventory, LMS, and HR modules.',
      'Constructed and maintained multi-tenant backend infrastructure using stancl/tenancy, enforcing strict data and workflow isolation across clients.',
      'Integrated HyperPay payment gateway and UltraMsg for automated transactional WhatsApp messaging.',
      'Boosted database response speeds by 60% through query refactoring, strategic B-tree indexing, and schema normalization.',
      'Configured Laravel Queues and background workers for heavy material cost computations and automated financial reporting without downtime.',
      'Managed company server environments on Contabo Linux; implemented security protocols, CORS handling, and SSL certificates.',
      'Facilitated team code reviews and version control workflows in Git, enforcing 100% compliance with PSR-12 coding standards.'
    ],
    stack: ['PHP (Expert)', 'Laravel (Expert)', 'stancl/tenancy', 'MySQL / MariaDB', 'HyperPay', 'UltraMsg', 'Contabo Linux', 'cPanel/WHM', 'Git']
  }
];

const SKILL_MATRIX = [
  {
    category: 'Backend Architecture',
    level: 'Core Expertise',
    skills: [
      { name: 'PHP (Expert)', primary: true },
      { name: 'Laravel (Expert)', primary: true },
      { name: 'RESTful API Design (600+ APIs)', primary: true },
      { name: 'Node.js (Integration Layer)', primary: false },
      { name: 'Sanctum API Authentication', primary: true }
    ]
  },
  {
    category: 'Database & Optimization',
    level: 'Core Expertise',
    skills: [
      { name: 'MySQL / MariaDB', primary: true },
      { name: '200+ Table Schema Design', primary: true },
      { name: 'Indexing & B-Tree Optimization', primary: true },
      { name: 'N+1 Query Elimination', primary: true },
      { name: 'Complex Query Refactoring', primary: true }
    ]
  },
  {
    category: 'Architecture & Design Patterns',
    level: 'Core Expertise',
    skills: [
      { name: 'Domain-Driven Design (DDD)', primary: true },
      { name: 'Multi-Tenancy (stancl/tenancy)', primary: true },
      { name: 'Service Layer & Repository Pattern', primary: true },
      { name: 'Laravel Queues & Background Workers', primary: true },
      { name: 'Atomic Locks & Overlap Protection', primary: true }
    ]
  },
  {
    category: 'DevOps & Server Administration',
    level: 'Infrastructure',
    skills: [
      { name: 'Contabo Linux Server Mgmt', primary: true },
      { name: 'Docker Containers', primary: false },
      { name: 'cPanel / WHM Administration', primary: true },
      { name: 'Cloudflare / DNS / SSL', primary: true },
      { name: 'CORS & Hardening Protocols', primary: true }
    ]
  },
  {
    category: 'Third-Party Integration Layer',
    level: 'Enterprise Connectors',
    skills: [
      { name: 'ERPNext Sync', primary: true },
      { name: 'MyFatoorah Payment Gateway', primary: true },
      { name: 'Aramex Logistics API', primary: true },
      { name: 'HyperPay Payment Gateway', primary: true },
      { name: 'VerifyWay Verification', primary: true },
      { name: 'WhatsApp Web API / UltraMsg', primary: true }
    ]
  },
  {
    category: 'Development Standards & Tools',
    level: 'Workflow',
    skills: [
      { name: 'PSR-12 Standard Compliance', primary: true },
      { name: 'Git / GitHub / GitLab', primary: true },
      { name: 'Postman API Testing', primary: true },
      { name: 'Firebase', primary: false }
    ]
  }
];

const API_PLAYGROUND_ENDPOINTS = [
  {
    id: 'recalculate-costs',
    method: 'POST',
    path: '/api/v1/erp/material-costs/recalculate',
    title: 'Recalculate Material Costs',
    description: 'Dispatches non-blocking async job with overlap protection for ERP financial engines.',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer 14|sanctum_token_8f92a1bc',
      'X-Tenant-ID': 'tenant_nexora_01'
    },
    body: {
      inventory_item_ids: [1042, 1043, 1089],
      recalculate_submodules: ['school_canteen', 'dining_pos', 'inventory_valuation'],
      apply_atomic_lock: true
    },
    response: {
      status: 202,
      statusText: 'Accepted',
      execution_time_ms: 11.4,
      memory_peak_mb: 4.2,
      data: {
        job_id: 'job_queue_mc_8849201',
        queue_name: 'financial-high-priority',
        overlap_protection: 'ENABLED',
        estimated_batches: 3,
        affected_tables: ['material_cost_logs', 'inventory_items', 'general_ledger_entries'],
        message: 'Material cost recalculation safely dispatched to background queue.'
      }
    },
    sqlTrace: [
      'SELECT * FROM `tenants` WHERE `id` = "tenant_nexora_01" LIMIT 1; /* 1.2ms */',
      'SELECT * FROM `inventory_items` WHERE `id` IN (1042, 1043, 1089) FOR UPDATE; /* 2.1ms */',
      'INSERT INTO `jobs` (`queue`, `payload`, `attempts`, `reserved_at`, `available_at`, `created_at`) VALUES (...); /* 1.8ms */'
    ]
  },
  {
    id: 'active-tenants',
    method: 'GET',
    path: '/api/v1/tenants/active-tenant-metrics',
    title: 'Multi-Tenant Isolation Metrics',
    description: 'Fetches isolated workspace statistics using stancl/tenancy domain scope.',
    headers: {
      'Accept': 'application/json',
      'Authorization': 'Bearer 14|sanctum_token_8f92a1bc'
    },
    body: null,
    response: {
      status: 200,
      statusText: 'OK',
      execution_time_ms: 8.6,
      memory_peak_mb: 3.8,
      data: {
        active_tenants: 12,
        tenancy_driver: 'stancl/tenancy-multi-database',
        schema_isolation: 'STRICT',
        tenant_database_prefix: 'tenant_db_',
        system_stats: {
          total_tables_per_tenant: 214,
          avg_query_time: '1.4ms',
          indexing_health: 'OPTIMAL (98.4%)'
        }
      }
    },
    sqlTrace: [
      'SELECT count(*) as aggregate FROM `tenants` WHERE `status` = "active"; /* 0.9ms */',
      'SELECT `id`, `domain`, `created_at` FROM `tenants` WHERE `deleted_at` IS NULL; /* 1.5ms */'
    ]
  },
  {
    id: 'aramex-shipment',
    method: 'POST',
    path: '/api/v1/integrations/aramex/create-shipment',
    title: 'Create Logistics Shipment',
    description: 'Outbound dispatch payload through the Araba integration gateway.',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer 14|sanctum_token_8f92a1bc',
      'X-Integration-Source': 'Araba_ECommerce'
    },
    body: {
      order_id: 'ORD-2026-9941',
      shipper: { name: 'Araba Logistics Hub', city: 'Damascus' },
      consignee: { name: 'Enterprise Client A', city: 'Aleppo' },
      packages_count: 2,
      total_weight_kg: 14.5
    },
    response: {
      status: 201,
      statusText: 'Created',
      execution_time_ms: 24.1,
      memory_peak_mb: 5.1,
      data: {
        shipment_number: 'ARMX-994820192',
        label_url: 'https://araba.platform/storage/labels/ARMX-994820192.pdf',
        erpnext_synced: true,
        myfatoorah_payment_status: 'PAID_INVOICE_8819',
        tracking_status: 'PACKAGE_MANIFEST_READY'
      }
    },
    sqlTrace: [
      'SELECT * FROM `orders` WHERE `id` = "ORD-2026-9941" LIMIT 1; /* 1.1ms */',
      'UPDATE `orders` SET `shipping_status` = "DISPATCHED", `tracking_code` = "ARMX-994820192" WHERE `id` = "ORD-2026-9941"; /* 2.4ms */'
    ]
  },
  {
    id: 'whatsapp-alert',
    method: 'POST',
    path: '/api/v1/notifications/whatsapp/send-transactional',
    title: 'WhatsApp Transactional Alert',
    description: 'Dispatches transactional alert via Laravel management layer and Node.js socket bridge.',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer 14|sanctum_token_8f92a1bc'
    },
    body: {
      recipient_phone: '+963936082400',
      template_id: 'tuition_fee_received',
      parameters: {
        student_name: 'Ahmad Al-Mansour',
        amount: '450,000 SYP',
        receipt_no: 'REC-2026-0041'
      }
    },
    response: {
      status: 200,
      statusText: 'OK',
      execution_time_ms: 14.8,
      memory_peak_mb: 4.0,
      data: {
        message_id: 'wamid.HBgLOTYzOTM2MDgyNDAwFQIAERgSMzBENDg1',
        gateway_bridge: 'Laravel_To_NodeJS_WhatsApp_Web',
        protocol_status: 'SENT_TO_SOCKET',
        delivery_acknowledgement: 'RECEIVED_BY_GATEWAY'
      }
    },
    sqlTrace: [
      'INSERT INTO `notification_logs` (`tenant_id`, `recipient`, `channel`, `status`) VALUES (...); /* 1.6ms */'
    ]
  }
];

export default function App() {
  const [activeNav, setActiveNav] = useState('overview');
  const [pipelineActiveStep, setPipelineActiveStep] = useState(0);
  const [isSimulatingRequest, setIsSimulatingRequest] = useState(false);
  const [selectedArchNode, setSelectedArchNode] = useState(ARCHITECTURE_NODES[2]); // Core as default
  const [selectedProject, setSelectedProject] = useState(PROJECTS[0]);
  const [projectTab, setProjectTab] = useState('summary'); // 'summary' | 'problem' | 'solution' | 'tech'
  const [selectedApi, setSelectedApi] = useState(API_PLAYGROUND_ENDPOINTS[0]);
  const [apiActiveTab, setApiActiveTab] = useState('response'); // 'response' | 'body' | 'headers' | 'sql'
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [terminalLogs, setTerminalLogs] = useState([
    '[INIT] System initialized: Moaz Alsouki Backend Architecture Portfolio v3.0',
    '[OK] Multi-tenant database connection established (stancl/tenancy)',
    '[OK] Laravel Sanctum Auth layer online',
    '[READY] Select an architectural node or endpoint to inspect real system telemetry'
  ]);

  // Request Pipeline Auto Simulation Step
  useEffect(() => {
    let interval;
    if (isSimulatingRequest) {
      interval = setInterval(() => {
        setPipelineActiveStep((prev) => {
          const next = (prev + 1) % 6;
          addTerminalLog(`[PIPELINE] Request moving through stage [${next + 1}/6]: ${ARCHITECTURE_NODES[next].title}`);
          if (next === 5) {
            setIsSimulatingRequest(false);
            addTerminalLog('[SUCCESS] Request processing pipeline completed in 12.4ms with 0 errors.');
          }
          return next;
        });
      }, 700);
    }
    return () => clearInterval(interval);
  }, [isSimulatingRequest]);

  const triggerRequestSimulation = () => {
    setPipelineActiveStep(0);
    setIsSimulatingRequest(true);
    addTerminalLog('[TRIGGER] Manual API request pipeline test initiated: POST /api/v1/erp/transaction');
  };

  const addTerminalLog = (msg) => {
    setTerminalLogs((prev) => [msg, ...prev.slice(0, 15)]);
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText('moazalsouki@gmail.com');
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2500);
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 font-sans selection:bg-cyan-500 selection:text-slate-950 relative overflow-x-hidden">
      {/* Background Subtle Grid & Lighting Effects */}
      <div 
        className="fixed inset-0 pointer-events-none opacity-20" 
        style={{
          backgroundImage: `radial-gradient(#1F293D 1px, transparent 1px), radial-gradient(#1F293D 1px, #0B0F17 1px)`,
          backgroundSize: '40px 40px',
          backgroundPosition: '0 0, 20px 20px'
        }}
      />
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[350px] bg-cyan-500/5 blur-[140px] pointer-events-none rounded-full" />

      {/* STICKY TOP NAVIGATION BAR */}
<header className="sticky top-0 z-50 bg-[#0F1115]/90 backdrop-blur-md border-b border-cyan-900/30 px-4 lg:px-8 py-3.5">
  <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
    
    {/* Left: Brand / Title */}
    <div className="flex items-center gap-3 shrink-0">
      <div className="w-9 h-9 rounded bg-cyan-950/80 border border-cyan-500/30 flex items-center justify-center font-mono text-cyan-400 font-bold text-sm shadow-sm shadow-cyan-950">
        MA
      </div>
      <div className="flex flex-col justify-center">
        <div className="flex items-center gap-2 leading-none">
          <span className="font-mono font-bold text-gray-100 tracking-tight text-sm">MOAZ ALSOUKI</span>
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-cyan-950 border border-cyan-800 text-cyan-400">v3.2</span>
        </div>
        <span className="text-[11px] font-mono text-gray-400 mt-1 leading-tight">Laravel Backend Architect</span>
      </div>
    </div>

    {/* Center: Status Pill (hidden on smaller screens) */}
    <div className="hidden xl:flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-950/40 border border-cyan-900/40 text-[11px] font-mono text-gray-300">
      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
      <span>AVAILABLE FOR BACKEND & ARCHITECTURE ROLES</span>
    </div>

    {/* Right: Nav Links */}
    <nav className="hidden lg:flex items-center gap-4 xl:gap-6 text-xs font-mono text-gray-400">
      <a href="#overview" className="hover:text-cyan-400 transition-colors">// OVERVIEW</a>
      <a href="#metrics" className="hover:text-cyan-400 transition-colors">// METRICS</a>
      <a href="#architecture" className="hover:text-cyan-400 transition-colors">// ARCHITECTURE</a>
      <a href="#projects" className="hover:text-cyan-400 transition-colors">// SYSTEMS</a>
      <a href="#experience" className="hover:text-cyan-400 transition-colors">// CHANGELOG</a>
      <a href="#contact" className="hover:text-cyan-400 transition-colors">// CONTACT</a>
    </nav>

    {/* CTA Button */}
    <a 
      href="#contact" 
      className="px-3.5 py-1.5 rounded text-xs font-mono font-semibold bg-cyan-950 hover:bg-cyan-900 text-cyan-400 border border-cyan-500/40 transition-all shadow-sm shadow-cyan-950 flex items-center gap-1.5 shrink-0"
    >
      <span className="text-cyan-500">&gt;_</span> INIT_CONTACT
    </a>

  </div>
</header>

      {}
      {/* HERO SECTION */}
      <section id="overview" className="relative pt-12 pb-20 lg:pt-20 lg:pb-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* CV Badge metadata strip */}
        <div className="inline-flex flex-wrap items-center gap-2 sm:gap-3 bg-[#111827] border border-[#1F293D] px-3 py-1.5 rounded-full text-xs font-mono text-slate-300 mb-8">
          <span className="flex items-center gap-1.5 text-cyan-400">
            <MapPin className="w-3.5 h-3.5" /> Damascus, Syria
          </span>
          <span className="text-slate-600">•</span>
          <span className="text-slate-200">3+ Years Enterprise Experience</span>
          <span className="text-slate-600">•</span>
          <span className="text-amber-400">PSR-12 Compliant</span>
          <span className="text-slate-600">•</span>
          <span className="text-emerald-400">Domain-Driven Architecture</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Main Editorial Copy */}
          <div className="lg:col-span-7 space-y-6">
            <div className="space-y-2">
              <h2 className="text-xs font-mono tracking-widest text-cyan-400 uppercase">
                // Senior Engineering Portfolio
              </h2>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-100 leading-tight">
                Moaz Alsouki
              </h1>
              <p className="text-xl sm:text-2xl font-semibold text-slate-400 font-mono">
                Laravel Backend Developer <span className="text-cyan-400">&</span> ERP Systems Architect
              </p>
            </div>

            <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
              Architecting high-concurrency ERP calculation engines, multi-tenant database infrastructures (<span className="text-cyan-400 font-mono font-medium">200+ tables</span>, <span className="text-cyan-400 font-mono font-medium">600+ REST APIs</span>), and resilient integration layers connecting enterprise ERPNext, financial payment gateways, logistics, and transactional messaging.
            </p>

            {/* Quick CTAs */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <a
                href="#architecture"
                className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold px-6 py-3 rounded text-sm font-mono flex items-center gap-2 transition-all shadow-[0_0_20px_rgba(0,229,255,0.3)] hover:shadow-[0_0_25px_rgba(0,229,255,0.5)]"
              >
                <Layers className="w-4 h-4" />
                EXPLORE_ARCHITECTURE
              </a>
              
              <a
                href="#projects"
                className="bg-[#111827] hover:bg-[#1A2338] text-slate-200 border border-[#1F293D] hover:border-cyan-500/50 px-6 py-3 rounded text-sm font-mono flex items-center gap-2 transition-all"
              >
                <FileText className="w-4 h-4 text-cyan-400" />
                CASE_STUDIES
              </a>

              <a
                href="#contact"
                className="text-slate-400 hover:text-slate-100 text-sm font-mono underline underline-offset-4 flex items-center gap-1.5"
              >
                DIRECT_CONTACT <ArrowRight className="w-3.5 h-3.5 text-cyan-400" />
              </a>
            </div>

            {/* Quick Tech Tag Cloud from CV */}
            <div className="pt-4 border-t border-[#1F293D]/60 flex flex-wrap items-center gap-2 font-mono text-xs text-slate-400">
              <span className="text-slate-500 font-bold uppercase">STACK:</span>
              {['PHP / Laravel', 'stancl/tenancy', 'MySQL / Indexing', 'ERPNext', 'MyFatoorah', 'Aramex', 'HyperPay', 'Docker / Linux'].map((t) => (
                <span key={t} className="bg-[#111827] border border-[#1F293D] px-2.5 py-1 rounded text-slate-300">
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* Interactive Request Pipeline Visualizer Widget */}
          <div className="lg:col-span-5 bg-[#111827]/90 border border-[#1F293D] rounded-xl p-5 shadow-2xl relative">
            
            <div className="flex items-center justify-between pb-4 border-b border-[#1F293D] mb-4">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-cyan-400 animate-pulse" />
                <span className="text-xs font-mono font-bold text-slate-200 uppercase tracking-wider">
                  Request Processing Pipeline
                </span>
              </div>
              <button
                onClick={triggerRequestSimulation}
                disabled={isSimulatingRequest}
                className="bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/40 px-2.5 py-1 rounded text-xs font-mono flex items-center gap-1.5 transition-all disabled:opacity-50"
              >
                <Play className="w-3 h-3 fill-cyan-400" />
                {isSimulatingRequest ? 'PROCESSING...' : 'TEST_PIPELINE'}
              </button>
            </div>

            {/* Visual Node Flow Steps */}
            <div className="space-y-2 font-mono text-xs">
              {[
                { label: '01. CLIENT REQUEST', sub: 'REST JSON / Mobile / E-Commerce', icon: Globe },
                { label: '02. SANCTUM AUTH & GATEWAY', sub: 'Token Validation / CORS / SSL', icon: ShieldCheck },
                { label: '03. DDD SERVICE LAYER', sub: 'Business Rules & Controllers', icon: Cpu },
                { label: '04. LARAVEL QUEUE WORKER', sub: 'Async Job & Atomic Lock', icon: Workflow },
                { label: '05. MULTI-TENANT DATABASE', sub: 'stancl/tenancy & MySQL Indexing', icon: Database },
                { label: '06. EXTERNAL INTEGRATIONS', sub: 'ERPNext / Payment / Logistics', icon: Network }
              ].map((step, idx) => {
                const Icon = step.icon;
                const isActive = pipelineActiveStep === idx;
                const isPassed = pipelineActiveStep > idx;

                return (
                  <div
                    key={step.label}
                    className={`p-2.5 rounded border transition-all flex items-center justify-between ${
                      isActive
                        ? 'bg-cyan-950/60 border-cyan-400 text-cyan-300 shadow-[0_0_12px_rgba(0,229,255,0.2)]'
                        : isPassed
                        ? 'bg-[#0B0F17] border-slate-800 text-emerald-400'
                        : 'bg-[#0B0F17]/50 border-[#1F293D]/60 text-slate-500'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-1.5 rounded ${isActive ? 'bg-cyan-400/20 text-cyan-300' : isPassed ? 'bg-emerald-950 text-emerald-400' : 'bg-slate-900 text-slate-600'}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="font-bold">{step.label}</div>
                        <div className="text-[10px] opacity-75">{step.sub}</div>
                      </div>
                    </div>
                    {isActive && (
                      <span className="flex items-center gap-1 text-[10px] bg-cyan-400 text-slate-950 font-bold px-1.5 py-0.5 rounded">
                        <RefreshCw className="w-2.5 h-2.5 animate-spin" /> ACTIVE
                      </span>
                    )}
                    {isPassed && (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Live Terminal Output Feed */}
            <div className="mt-4 bg-[#070A0F] border border-[#1F293D] rounded p-3 font-mono text-[11px] text-slate-400 space-y-1 h-28 overflow-y-auto">
              <div className="text-slate-500 text-[10px] uppercase tracking-wider mb-1 flex items-center justify-between">
                <span>// Telemetry Stream</span>
                <span className="text-cyan-400">STATUS: ONLINE</span>
              </div>
              {terminalLogs.map((log, i) => (
                <div key={i} className={i === 0 ? 'text-cyan-300 font-bold' : 'text-slate-400'}>
                  {log}
                </div>
              ))}
            </div>

          </div>

        </div>
      </section>

      {}
      {/* SYSTEM PERFORMANCE METRICS */}
      <section id="metrics" className="py-16 bg-[#0E1420] border-y border-[#1F293D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="mb-10 text-center sm:text-left">
            <h2 className="text-xs font-mono text-cyan-400 uppercase tracking-widest">// Quantified Impact</h2>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-100 tracking-tight">
              Engineering Metrics & Database Telemetry
            </h3>
            <p className="text-sm text-slate-400 font-mono mt-1">
              Real metrics derived directly from production ERP platforms and legacy migrations.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                metric: '3+ Years',
                label: 'Backend & ERP Architecture',
                sub: 'Building & scaling multi-tenant financial engines in Laravel',
                icon: Calendar,
                accent: 'text-cyan-400'
              },
              {
                metric: '200+',
                label: 'Database Tables Designed',
                sub: 'Complex relational schemas for Inventory, Accounting, LMS & HR',
                icon: Database,
                accent: 'text-emerald-400'
              },
              {
                metric: '600+',
                label: 'RESTful APIs Engineered',
                sub: 'Secured via Sanctum with uniform response conventions',
                icon: Code2,
                accent: 'text-cyan-400'
              },
              {
                metric: '60%',
                label: 'Database Response Speedup',
                sub: 'Achieved through query refactoring, indexing & N+1 removal',
                icon: Zap,
                accent: 'text-amber-400'
              },
              {
                metric: '40%',
                label: 'Admin Overhead Reduced',
                sub: 'Automated exam workflows, bus routes, and HR processes',
                icon: CheckSquare,
                accent: 'text-purple-400'
              },
              {
                metric: '100%',
                label: 'Financial Engine Accuracy',
                sub: 'Material cost recalculations with atomic lock overlap protection',
                icon: ShieldCheck,
                accent: 'text-emerald-400'
              }
            ].map((stat, i) => {
              const Icon = stat.icon;
              return (
                <div
                  key={i}
                  className="bg-[#111827] border border-[#1F293D] p-6 rounded-xl hover:border-cyan-500/40 transition-all group"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className={`p-2.5 rounded-lg bg-[#0B0F17] border border-[#1F293D] ${stat.accent}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-mono text-slate-500">SYSTEM_METRIC_0{i + 1}</span>
                  </div>
                  <div className={`text-3xl sm:text-4xl font-extrabold font-mono tracking-tight mb-1 ${stat.accent}`}>
                    {stat.metric}
                  </div>
                  <div className="text-base font-bold text-slate-200 mb-1">
                    {stat.label}
                  </div>
                  <div className="text-xs text-slate-400 font-mono">
                    {stat.sub}
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {}
      {/* SYSTEM ARCHITECTURE VISUALIZER */}
      <section id="architecture" className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="mb-12">
          <div className="flex items-center gap-2 text-xs font-mono text-cyan-400 uppercase tracking-widest mb-1">
            <Workflow className="w-4 h-4" />
            <span>// System Architecture Inspection</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-100 tracking-tight">
            How I Build Backend Systems
          </h2>
          <p className="text-slate-400 font-mono text-sm mt-2 max-w-3xl">
            Click on any architectural node in the blueprint below to inspect the exact specs, technologies, and production implementations from Moaz's CV.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Architectural Node Selection Buttons (Left/Top) */}
          <div className="lg:col-span-5 space-y-3">
            <div className="text-xs font-mono text-slate-400 uppercase tracking-wider mb-2 flex items-center justify-between">
              <span>Interactive Nodes</span>
              <span className="text-cyan-400">[SELECT TO INSPECT]</span>
            </div>

            {ARCHITECTURE_NODES.map((node, index) => {
              const Icon = node.icon;
              const isSelected = selectedArchNode.id === node.id;

              return (
                <button
                  key={node.id}
                  onClick={() => setSelectedArchNode(node)}
                  className={`w-full text-left p-4 rounded-xl border transition-all flex items-start gap-4 ${
                    isSelected
                      ? 'bg-[#151E33] border-cyan-400 text-slate-100 shadow-[0_0_20px_rgba(0,229,255,0.15)]'
                      : 'bg-[#111827] border-[#1F293D] text-slate-300 hover:border-slate-700 hover:bg-[#131B2E]'
                  }`}
                >
                  <div className={`p-2.5 rounded-lg font-mono text-xs ${isSelected ? 'bg-cyan-500 text-slate-950 font-bold' : 'bg-[#0B0F17] text-cyan-400 border border-[#1F293D]'}`}>
                    0{index + 1}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono text-cyan-400">{node.type}</span>
                      {isSelected && <ChevronRight className="w-4 h-4 text-cyan-400" />}
                    </div>
                    <div className="font-bold text-sm text-slate-100 truncate mt-0.5">
                      {node.title}
                    </div>
                    <div className="text-xs text-slate-400 font-mono truncate mt-1">
                      {node.tag}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Node Inspector Display Panel (Right) */}
          <div className="lg:col-span-7 bg-[#111827] border border-[#1F293D] rounded-xl p-6 sm:p-8 relative">
            
            <div className="flex items-center justify-between pb-6 border-b border-[#1F293D] mb-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg text-cyan-400">
                  <selectedArchNode.icon className="w-6 h-6" />
                </div>
                <div>
                  <span className="text-xs font-mono text-cyan-400 uppercase">{selectedArchNode.type}</span>
                  <h3 className="text-xl font-extrabold text-slate-100">{selectedArchNode.title}</h3>
                </div>
              </div>
              <span className="hidden sm:inline-block text-xs font-mono bg-[#0B0F17] border border-[#1F293D] px-3 py-1 rounded text-slate-400">
                INSPECTOR_ACTIVE
              </span>
            </div>

            {/* Description & CV Evidence */}
            <div className="space-y-6">
              <div>
                <h4 className="text-xs font-mono text-slate-400 uppercase tracking-wider mb-2">// Overview</h4>
                <p className="text-sm text-slate-300 leading-relaxed">
                  {selectedArchNode.shortDesc}
                </p>
              </div>

              {/* CV Evidence Box */}
              <div className="bg-cyan-950/30 border border-cyan-800/40 rounded-lg p-4 font-mono text-xs">
                <div className="text-cyan-400 font-bold mb-1 flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5" />
                  CV PROOF OF IMPLEMENTATION:
                </div>
                <div className="text-slate-300 leading-relaxed italic">
                  "{selectedArchNode.cvHighlight}"
                </div>
              </div>

              {/* Technical Specifications */}
              <div>
                <h4 className="text-xs font-mono text-slate-400 uppercase tracking-wider mb-3">// Technical Specifications</h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-mono text-slate-300">
                  {selectedArchNode.specifications.map((spec, i) => (
                    <li key={i} className="flex items-start gap-2 bg-[#0B0F17] p-2.5 rounded border border-[#1F293D]">
                      <Check className="w-3.5 h-3.5 text-cyan-400 shrink-0 mt-0.5" />
                      <span>{spec}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Tech Stack Pills */}
              <div>
                <h4 className="text-xs font-mono text-slate-400 uppercase tracking-wider mb-2">// Technologies & Drivers</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedArchNode.tech.map((t) => (
                    <span key={t} className="bg-[#0B0F17] text-cyan-300 border border-cyan-500/30 px-3 py-1 rounded-md text-xs font-mono">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>

          </div>

        </div>
      </section>

      {}
      {/* FEATURED PROJECTS & CASE STUDIES */}
      <section id="projects" className="py-20 bg-[#0E1420] border-t border-[#1F293D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="mb-12">
            <h2 className="text-xs font-mono text-cyan-400 uppercase tracking-widest">// Enterprise Case Studies</h2>
            <h3 className="text-3xl sm:text-4xl font-extrabold text-slate-100 tracking-tight mt-1">
              Featured Systems & Engineering Projects
            </h3>
            <p className="text-slate-400 font-mono text-sm mt-2 max-w-2xl">
              In-depth architectural breakdowns of systems migrated, designed, and scaled by Moaz Alsouki.
            </p>
          </div>

          {/* Project Selector Tabs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {PROJECTS.map((proj) => {
              const isSelected = selectedProject.id === proj.id;
              return (
                <button
                  key={proj.id}
                  onClick={() => {
                    setSelectedProject(proj);
                    setProjectTab('summary');
                  }}
                  className={`p-4 rounded-xl border text-left transition-all font-mono text-xs ${
                    isSelected
                      ? 'bg-cyan-950/50 border-cyan-400 text-slate-100 shadow-[0_0_15px_rgba(0,229,255,0.2)]'
                      : 'bg-[#111827] border-[#1F293D] text-slate-400 hover:text-slate-200 hover:border-slate-700'
                  }`}
                >
                  <div className="text-[10px] text-cyan-400 uppercase font-bold mb-1">{proj.badge}</div>
                  <div className="font-bold text-sm text-slate-200 line-clamp-1">{proj.title}</div>
                  <div className="text-[11px] text-slate-500 mt-2">{proj.domain}</div>
                </button>
              );
            })}
          </div>

          {/* Active Project Case Study Card */}
          <div className="bg-[#111827] border border-[#1F293D] rounded-2xl p-6 sm:p-10">
            
            {/* Header info */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-6 border-b border-[#1F293D]">
              <div>
                <div className="inline-block bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 px-3 py-1 rounded text-xs font-mono mb-3">
                  {selectedProject.badge}
                </div>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-100">
                  {selectedProject.title}
                </h3>
                <p className="text-xs font-mono text-slate-400 mt-1">
                  ROLE: <span className="text-slate-200">{selectedProject.role}</span> | DOMAIN: <span className="text-cyan-400">{selectedProject.domain}</span>
                </p>
              </div>

              {/* Sub-tabs inside active case study */}
              <div className="flex flex-wrap gap-2 bg-[#0B0F17] p-1.5 rounded-lg border border-[#1F293D] font-mono text-xs">
                {[
                  { id: 'summary', label: 'OVERVIEW' },
                  { id: 'problem', label: 'THE_PROBLEM' },
                  { id: 'solution', label: 'ARCHITECTURE' },
                  { id: 'tech', label: 'TECH_STACK' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setProjectTab(tab.id)}
                    className={`px-3 py-1.5 rounded transition-all ${
                      projectTab === tab.id
                        ? 'bg-cyan-500 text-slate-950 font-bold'
                        : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Dynamic Content Body according to active sub-tab */}
            <div className="py-6 min-h-[180px]">
              {projectTab === 'summary' && (
                <div className="space-y-4">
                  <p className="text-base text-slate-300 leading-relaxed">
                    {selectedProject.summary}
                  </p>
                  <div className="p-4 bg-[#0B0F17] rounded-xl border border-[#1F293D]">
                    <span className="text-xs font-mono text-cyan-400 font-bold block mb-1">// Architectural Detail</span>
                    <p className="text-xs font-mono text-slate-300">
                      {selectedProject.architectureDetails}
                    </p>
                  </div>
                </div>
              )}

              {projectTab === 'problem' && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-amber-400 font-mono text-xs font-bold uppercase">
                    <AlertTriangle className="w-4 h-4" /> Operational & Engineering Challenges:
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed bg-[#0B0F17] p-5 rounded-xl border border-amber-900/30">
                    {selectedProject.problem}
                  </p>
                </div>
              )}

              {projectTab === 'solution' && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-emerald-400 font-mono text-xs font-bold uppercase">
                    <CheckCircle2 className="w-4 h-4" /> Architected Solution:
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed bg-[#0B0F17] p-5 rounded-xl border border-emerald-900/30">
                    {selectedProject.solution}
                  </p>
                </div>
              )}

              {projectTab === 'tech' && (
                <div>
                  <div className="text-xs font-mono text-slate-400 uppercase mb-3">// Technologies & Integrations Used</div>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.tech.map((t) => (
                      <span key={t} className="bg-[#0B0F17] text-cyan-300 border border-cyan-500/30 px-3 py-1.5 rounded-lg text-xs font-mono">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Project Metrics Footer */}
            <div className="pt-6 border-t border-[#1F293D] grid grid-cols-1 sm:grid-cols-3 gap-4">
              {selectedProject.metrics.map((m, idx) => (
                <div key={idx} className="bg-[#0B0F17] border border-[#1F293D] p-4 rounded-xl">
                  <div className="text-xs font-mono text-slate-400">{m.label}</div>
                  <div className="text-xl sm:text-2xl font-extrabold font-mono text-cyan-400 mt-1">{m.value}</div>
                </div>
              ))}
            </div>

          </div>

        </div>
      </section>

      {}
      {/* PROFESSIONAL EXPERIENCE (ENGINEERING CHANGELOG STYLE) */}
      <section id="experience" className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="mb-12">
          <h2 className="text-xs font-mono text-cyan-400 uppercase tracking-widest">// System Release Log</h2>
          <h3 className="text-3xl sm:text-4xl font-extrabold text-slate-100 tracking-tight mt-1">
            Professional Experience & System Releases
          </h3>
          <p className="text-slate-400 font-mono text-sm mt-2">
            Chronological engineering history formatted as release tags and platform updates.
          </p>
        </div>

        <div className="space-y-8 relative">
          
          {/* Timeline Connector Line */}
          <div className="hidden lg:block absolute top-0 bottom-0 left-[27px] w-0.5 bg-[#1F293D]" />

          {EXPERIENCE_LOG.map((exp, idx) => (
            <div key={idx} className="relative pl-0 lg:pl-16">
              
              {/* Timeline Dot Marker */}
              <div className="hidden lg:flex absolute left-0 top-6 w-14 h-14 items-center justify-center">
                <div className="w-4 h-4 rounded-full bg-cyan-500 ring-4 ring-[#0B0F17] shadow-[0_0_12px_rgba(0,229,255,0.6)]" />
              </div>

              <div className="bg-[#111827] border border-[#1F293D] rounded-xl p-6 sm:p-8 hover:border-cyan-500/40 transition-all">
                
                {/* Header Tag */}
                <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-[#1F293D] mb-6">
                  <div>
                    <div className="flex items-center gap-2 font-mono text-xs text-cyan-400">
                      <span>{exp.version}</span>
                      <span>•</span>
                      <span className="text-amber-400">{exp.status}</span>
                    </div>
                    <h4 className="text-xl sm:text-2xl font-extrabold text-slate-100 mt-1">
                      {exp.role} <span className="text-cyan-400">@ {exp.company}</span>
                    </h4>
                  </div>
                  <div className="text-right font-mono text-xs text-slate-400">
                    <div className="text-slate-200 font-bold">{exp.period}</div>
                    <div>{exp.location}</div>
                  </div>
                </div>

                {/* Highlights List */}
                <ul className="space-y-3 mb-6 font-sans text-sm text-slate-300">
                  {exp.highlights.map((item, hIdx) => (
                    <li key={hIdx} className="flex items-start gap-3">
                      <ChevronRight className="w-4 h-4 text-cyan-400 shrink-0 mt-1" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                {/* Stack Used */}
                <div className="pt-4 border-t border-[#1F293D]/60 flex flex-wrap items-center gap-2 font-mono text-xs">
                  <span className="text-slate-500 font-bold uppercase">ENV_STACK:</span>
                  {exp.stack.map((s) => (
                    <span key={s} className="bg-[#0B0F17] text-slate-300 border border-[#1F293D] px-2.5 py-1 rounded">
                      {s}
                    </span>
                  ))}
                </div>

              </div>

            </div>
          ))}

        </div>
      </section>

      {}
      {/* TECHNICAL STACK & MATRIX */}
      <section id="expertise" className="py-20 bg-[#0E1420] border-t border-[#1F293D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="mb-12">
            <h2 className="text-xs font-mono text-cyan-400 uppercase tracking-widest">// Technical Capabilities</h2>
            <h3 className="text-3xl sm:text-4xl font-extrabold text-slate-100 tracking-tight mt-1">
              Domain Expertise & Technology Matrix
            </h3>
            <p className="text-slate-400 font-mono text-sm mt-2">
              Strictly derived from real project implementation and production server maintenance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SKILL_MATRIX.map((group, idx) => (
              <div
                key={idx}
                className="bg-[#111827] border border-[#1F293D] p-6 rounded-xl hover:border-cyan-500/30 transition-all flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between pb-3 border-b border-[#1F293D] mb-4">
                    <h4 className="font-bold text-slate-100 text-base">{group.category}</h4>
                    <span className="text-[10px] font-mono bg-cyan-950/60 text-cyan-400 border border-cyan-800/50 px-2 py-0.5 rounded">
                      {group.level}
                    </span>
                  </div>

                  <div className="space-y-2 mb-4">
                    {group.skills.map((skill, sIdx) => (
                      <div
                        key={sIdx}
                        className={`p-2.5 rounded text-xs font-mono flex items-center justify-between ${
                          skill.primary
                            ? 'bg-[#0B0F17] border border-cyan-500/20 text-slate-200'
                            : 'bg-[#0B0F17]/50 border border-[#1F293D] text-slate-400'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <Check className={`w-3.5 h-3.5 ${skill.primary ? 'text-cyan-400' : 'text-slate-600'}`} />
                          {skill.name}
                        </span>
                        {skill.primary && (
                          <span className="text-[9px] text-cyan-400 uppercase font-bold">CORE</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {}
      {/* TECHNICAL PHILOSOPHY & ARCHITECTURE PRINCIPLES */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="mb-12">
          <h2 className="text-xs font-mono text-cyan-400 uppercase tracking-widest">// Core Architecture Postulates</h2>
          <h3 className="text-3xl sm:text-4xl font-extrabold text-slate-100 tracking-tight mt-1">
            Engineering Principles
          </h3>
          <p className="text-slate-400 font-mono text-sm mt-2 max-w-2xl">
            My approach to building software systems that remain operational under load and maintainable across long lifecycles.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            {
              num: '01',
              title: 'Multi-Tenant Data Isolation & Security First',
              desc: 'Financial and administrative ERP systems require complete client data segregation. Utilizing stancl/tenancy, database scoping, and Sanctum auth prevents cross-tenant leakage at the schema level.'
            },
            {
              num: '02',
              title: 'Non-Blocking Async Queues for Heavy Calculations',
              desc: 'Heavy material recalculations, invoice batching, and notification dispatches must never block HTTP controllers. Atomic locks and background queue workers guarantee sub-second API responses.'
            },
            {
              num: '03',
              title: 'Zero-Downtime Legacy Framework Migrations',
              desc: 'Migrating legacy platforms (e.g. Symfony to Laravel) demands maintaining live service continuity, translating database schemas cleanly, and maintaining backward-compatible API contracts.'
            },
            {
              num: '04',
              title: 'Domain-Driven Clean Architecture over Monoliths',
              desc: 'Separating business rules into dedicated Service Layers and Repositories ensures that 200+ database tables and 600+ APIs remain readable, testable, and compliant with PSR-12 standards.'
            }
          ].map((principle) => (
            <div
              key={principle.num}
              className="bg-[#111827] border border-[#1F293D] p-6 sm:p-8 rounded-xl hover:border-cyan-500/30 transition-all"
            >
              <div className="text-3xl font-mono font-extrabold text-cyan-400 mb-3">
                //{principle.num}
              </div>
              <h4 className="text-xl font-bold text-slate-100 mb-2">
                {principle.title}
              </h4>
              <p className="text-sm text-slate-300 leading-relaxed font-sans">
                {principle.desc}
              </p>
            </div>
          ))}
        </div>

      </section>

      {}
      {/* INTERACTIVE API INSPECTOR PLAYGROUND */}
      <section id="inspector" className="py-20 bg-[#0E1420] border-y border-[#1F293D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="mb-10">
            <div className="flex items-center gap-2 text-xs font-mono text-cyan-400 uppercase tracking-widest mb-1">
              <Terminal className="w-4 h-4" />
              <span>// Live API & Query Telemetry Inspector</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-100 tracking-tight">
              Interactive Endpoint Playground
            </h2>
            <p className="text-slate-400 font-mono text-sm mt-2 max-w-2xl">
              Inspect mock REST API payloads, execution timings, and SQL query executions built by Moaz Alsouki.
            </p>
          </div>

          <div className="bg-[#111827] border border-[#1F293D] rounded-xl overflow-hidden shadow-2xl">
            
            {/* Top Bar Endpoint Buttons */}
            <div className="p-4 bg-[#0B0F17] border-b border-[#1F293D] flex flex-wrap items-center gap-2">
              <span className="text-xs font-mono text-slate-500 mr-2 uppercase">// Select Endpoint:</span>
              {API_PLAYGROUND_ENDPOINTS.map((ep) => (
                <button
                  key={ep.id}
                  onClick={() => setSelectedApi(ep)}
                  className={`px-3 py-1.5 rounded text-xs font-mono transition-all flex items-center gap-2 ${
                    selectedApi.id === ep.id
                      ? 'bg-cyan-500 text-slate-950 font-bold'
                      : 'bg-[#111827] text-slate-300 border border-[#1F293D] hover:border-slate-700'
                  }`}
                >
                  <span className={`px-1 rounded text-[10px] ${ep.method === 'POST' ? 'bg-emerald-950 text-emerald-400 font-bold' : 'bg-blue-950 text-blue-400 font-bold'}`}>
                    {ep.method}
                  </span>
                  <span>{ep.title}</span>
                </button>
              ))}
            </div>

            {/* Request Address Bar Display */}
            <div className="p-4 bg-[#0E1420] border-b border-[#1F293D] flex flex-wrap items-center justify-between gap-4 font-mono text-xs">
              <div className="flex items-center gap-2 min-w-0">
                <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-bold">
                  {selectedApi.method}
                </span>
                <span className="text-slate-100 font-bold truncate">{selectedApi.path}</span>
              </div>

              <div className="flex items-center gap-4 text-slate-400">
                <div>EXEC_TIME: <span className="text-cyan-400 font-bold">{selectedApi.response.execution_time_ms}ms</span></div>
                <div>PEAK_MEM: <span className="text-emerald-400 font-bold">{selectedApi.response.memory_peak_mb}MB</span></div>
                <div className="px-2 py-0.5 rounded bg-emerald-950 text-emerald-400 border border-emerald-800/50">
                  HTTP {selectedApi.response.status} {selectedApi.response.statusText}
                </div>
              </div>
            </div>

            {/* Inspector Payload / SQL Tabs */}
            <div className="grid grid-cols-1 lg:grid-cols-12">
              
              {/* Left Column: Tab Controls & Description */}
              <div className="lg:col-span-4 p-6 border-b lg:border-b-0 lg:border-r border-[#1F293D] bg-[#0B0F17]/50 space-y-6">
                <div>
                  <h4 className="text-sm font-bold text-slate-100 mb-1">{selectedApi.title}</h4>
                  <p className="text-xs text-slate-400 leading-relaxed font-sans">
                    {selectedApi.description}
                  </p>
                </div>

                <div className="space-y-2 font-mono text-xs">
                  <div className="text-slate-500 uppercase">// Inspector View mode</div>
                  {[
                    { id: 'response', label: '01. RESPONSE_PAYLOAD' },
                    { id: 'body', label: '02. REQUEST_BODY' },
                    { id: 'headers', label: '03. REQUEST_HEADERS' },
                    { id: 'sql', label: '04. SQL_QUERY_TRACE' }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setApiActiveTab(tab.id)}
                      className={`w-full text-left p-2.5 rounded transition-all ${
                        apiActiveTab === tab.id
                          ? 'bg-cyan-950/80 border border-cyan-400 text-cyan-300 font-bold'
                          : 'bg-[#111827] text-slate-400 hover:text-slate-200 border border-[#1F293D]'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Right Column: Code JSON / SQL Output Display */}
              <div className="lg:col-span-8 p-6 bg-[#070A0F] font-mono text-xs overflow-x-auto min-h-[260px]">
                {apiActiveTab === 'response' && (
                  <pre className="text-cyan-300">
                    {JSON.stringify(selectedApi.response, null, 2)}
                  </pre>
                )}

                {apiActiveTab === 'body' && (
                  <pre className="text-slate-300">
                    {selectedApi.body ? JSON.stringify(selectedApi.body, null, 2) : '// No request body required for GET'}
                  </pre>
                )}

                {apiActiveTab === 'headers' && (
                  <pre className="text-amber-300">
                    {JSON.stringify(selectedApi.headers, null, 2)}
                  </pre>
                )}

                {apiActiveTab === 'sql' && (
                  <div className="space-y-3">
                    <div className="text-slate-500 text-[10px] uppercase">// Database Query Execution Log (Optimized B-Tree Indexing)</div>
                    {selectedApi.sqlTrace.map((query, i) => (
                      <div key={i} className="p-3 bg-[#0B0F17] rounded border border-[#1F293D] text-emerald-400 font-mono text-[11px]">
                        {query}
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>

          </div>

        </div>
      </section>

      {}
      {/* CONTACT & SERVER TERMINAL GATEWAY */}
      <section id="contact" className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="bg-[#111827] border border-[#1F293D] rounded-2xl p-6 sm:p-12 relative overflow-hidden shadow-2xl">
          
          <div className="max-w-3xl space-y-6">
            <div className="inline-flex items-center gap-2 text-xs font-mono bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 px-3 py-1 rounded">
              <Terminal className="w-3.5 h-3.5" /> READY_TO_BUILD_THE_NEXT_SYSTEM?
            </div>

            <h2 className="text-3xl sm:text-5xl font-extrabold text-slate-100 tracking-tight leading-tight">
              Let's Discuss Your Backend Architecture
            </h2>

            <p className="text-slate-300 text-base sm:text-lg leading-relaxed font-sans">
              Currently open to senior engineering roles, high-concurrency ERP architecture design, legacy Laravel migrations, and complex multi-tenant system implementations.
            </p>

            {/* Direct Contact Buttons */}
            <div className="pt-4 flex flex-wrap items-center gap-4 font-mono text-sm">
              <button
                onClick={handleCopyEmail}
                className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold px-6 py-3.5 rounded-lg flex items-center gap-2 transition-all shadow-[0_0_20px_rgba(0,229,255,0.3)]"
              >
                {copiedEmail ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copiedEmail ? 'EMAIL_COPIED_TO_CLIPBOARD' : 'COPY: moazalsouki@gmail.com'}
              </button>

              <a
                href="mailto:moazalsouki@gmail.com"
                className="bg-[#0B0F17] hover:bg-[#151E33] text-slate-100 border border-[#1F293D] hover:border-cyan-500/50 px-6 py-3.5 rounded-lg flex items-center gap-2 transition-all"
              >
                <Mail className="w-4 h-4 text-cyan-400" />
                SEND_DIRECT_EMAIL
              </a>
            </div>

            {/* CV Direct Contact Strip */}
            <div className="pt-8 border-t border-[#1F293D] grid grid-cols-1 sm:grid-cols-3 gap-4 font-mono text-xs text-slate-300">
              <div className="flex items-center gap-2.5 bg-[#0B0F17] p-3 rounded border border-[#1F293D]">
                <Phone className="w-4 h-4 text-cyan-400 shrink-0" />
                <span>+963 936 082 400</span>
              </div>

              <a
                href="https://linkedin.com/in/moaz-alsouki"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2.5 bg-[#0B0F17] p-3 rounded border border-[#1F293D] hover:border-cyan-400 transition-all text-slate-300 hover:text-cyan-400"
              >
                <Linkedin className="w-4 h-4 text-cyan-400 shrink-0" />
                <span>linkedin.com/in/moaz-alsouki</span>
              </a>

              <div className="flex items-center gap-2.5 bg-[#0B0F17] p-3 rounded border border-[#1F293D]">
                <MapPin className="w-4 h-4 text-cyan-400 shrink-0" />
                <span>Damascus, Syria</span>
              </div>
            </div>

          </div>

        </div>

      </section>

      {/* MINIMAL FOOTER */}
      <footer className="py-8 border-t border-[#1F293D] bg-[#070A0F] font-mono text-xs text-slate-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <span className="text-slate-200 font-bold">MOAZ ALSOUKI</span> — Bachelor of Information Technology Engineering, Damascus University (2023)
          </div>

          <div className="flex items-center gap-4 text-slate-400">
            <span>PSR-12 COMPLIANT</span>
            <span>•</span>
            <span>LARAVEL & ERP ARCHITECT</span>
            <span>•</span>
            <a href="#overview" className="text-cyan-400 hover:underline">BACK_TO_TOP ↑</a>
          </div>
        </div>
      </footer>

    </div>
  );
}